// Auto-draft persistence.
// - Uses sessionStorage (cleared on tab close). NOT persistent — matches
//   the personal-use / security-conscious guidance from the user.
// - Explicitly refuses to store auth/security fields.
// - Cleared on logout via `clearAllDrafts()`.
const PREFIX = "emlak-draft-";
const TTL_MS = 24 * 60 * 60 * 1000; // 24h within session

// Fields that must NEVER be persisted client-side, even if a caller passes them.
const FORBIDDEN_KEYS = new Set([
  "password", "current_password", "new_password", "confirm_password",
  "totp_code", "totp_secret", "recovery_code", "recovery_codes",
  "access_token", "refresh_token", "jwt", "token", "session",
  "avatar_url", "avatar_base64", "cookie", "authorization",
]);

function sanitizeDraft(data) {
  if (!data || typeof data !== "object") return data;
  const out = Array.isArray(data) ? [] : {};
  for (const [k, v] of Object.entries(data)) {
    if (FORBIDDEN_KEYS.has(String(k).toLowerCase())) continue;
    out[k] = v && typeof v === "object" ? sanitizeDraft(v) : v;
  }
  return out;
}

function safeStore() {
  try { return window.sessionStorage; } catch { return null; }
}

export function saveDraft(key, data) {
  const store = safeStore();
  if (!store) return;
  try {
    const clean = sanitizeDraft(data);
    store.setItem(PREFIX + key, JSON.stringify({ data: clean, savedAt: Date.now() }));
  } catch (e) {
    console.debug("Draft save failed:", e?.message || e);
  }
}

export function loadDraft(key) {
  const store = safeStore();
  if (!store) return null;
  try {
    const raw = store.getItem(PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.savedAt > TTL_MS) {
      store.removeItem(PREFIX + key);
      return null;
    }
    return parsed.data;
  } catch (e) {
    console.debug("Draft load failed:", e?.message || e);
    return null;
  }
}

export function clearDraft(key) {
  const store = safeStore();
  if (!store) return;
  try { store.removeItem(PREFIX + key); }
  catch (e) { console.debug("Draft clear failed:", e?.message || e); }
}

/** Called on logout — removes any lingering drafts. */
export function clearAllDrafts() {
  const store = safeStore();
  if (!store) return;
  try {
    const keys = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (k && k.startsWith(PREFIX)) keys.push(k);
    }
    keys.forEach((k) => store.removeItem(k));
  } catch (e) {
    console.debug("clearAllDrafts failed:", e?.message || e);
  }
}

/* ============================================================
 * PENDING SYNC QUEUE (localStorage — survives tab close)
 * ------------------------------------------------------------
 * Persists Quick Field Entries that could not be POSTed to the
 * server (network failure, session expiry, etc.). Each entry
 * gets a client-side idempotency key. On next successful sync
 * the item is removed from the queue.
 * ============================================================ */
const PENDING_KEY = "emlak-pending-quick-v1";

function localStore() {
  try { return window.localStorage; } catch { return null; }
}

export function getPendingQuickEntries() {
  const s = localStore();
  if (!s) return [];
  try {
    const raw = s.getItem(PENDING_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch { return []; }
}

function _writePending(list) {
  const s = localStore();
  if (!s) throw new Error("localStorage unavailable");
  try {
    s.setItem(PENDING_KEY, JSON.stringify(list));
  } catch (e) {
    // QuotaExceededError or SecurityError — bubble up so the caller can
    // surface a real error to the user instead of showing a fake
    // "Cihazda bekliyor" success.
    throw e;
  }
}

function _containsBinary(v, seen = new WeakSet()) {
  if (v === null || v === undefined) return false;
  if (typeof v !== "object") return false;
  // File/Blob/FileList detection — these do not JSON-serialize safely.
  try {
    if (typeof Blob !== "undefined" && v instanceof Blob) return true;
    if (typeof File !== "undefined" && v instanceof File) return true;
    if (typeof FileList !== "undefined" && v instanceof FileList) return true;
    if (typeof ArrayBuffer !== "undefined" && (v instanceof ArrayBuffer || ArrayBuffer.isView(v))) return true;
  } catch {}
  if (seen.has(v)) return false;
  seen.add(v);
  if (Array.isArray(v)) return v.some(x => _containsBinary(x, seen));
  for (const k of Object.keys(v)) {
    if (_containsBinary(v[k], seen)) return true;
  }
  return false;
}

/** Push a failed quick-entry into the pending queue. Idempotent by clientKey.
 *  Throws if localStorage is unavailable, quota-exceeded, or the payload
 *  contains non-JSON-serializable data (Blob/File etc.). Callers MUST
 *  handle the exception and surface a real error state to the user.
 */
export function pushPendingQuickEntry(clientKey, payload, userScope = "self") {
  if (!clientKey || !payload) throw new Error("clientKey and payload required");
  // Refuse to queue anything that carries binary/File/Blob refs — we don't
  // reliably persist those in localStorage. Photos flow through a separate
  // photo-upload endpoint after the property is created.
  if (_containsBinary(payload)) {
    throw new Error("PAYLOAD_HAS_BINARY");
  }
  const clean = sanitizeDraft(payload);
  const list = getPendingQuickEntries();
  const existing = list.findIndex(e => e.clientKey === clientKey);
  const entry = {
    clientKey,
    userScope,
    payload: clean,
    createdAt: Date.now(),
    lastAttemptAt: Date.now(),
    attempts: existing >= 0 ? (list[existing].attempts || 0) + 1 : 1,
  };
  if (existing >= 0) list[existing] = entry; else list.push(entry);
  _writePending(list);
}

/** Remove a pending entry after successful sync. */
export function removePendingQuickEntry(clientKey) {
  if (!clientKey) return;
  const list = getPendingQuickEntries().filter(e => e.clientKey !== clientKey);
  _writePending(list);
}

/** Generate a stable client key for idempotency. */
export function newClientKey() {
  try {
    if (window.crypto?.randomUUID) return `qf-${window.crypto.randomUUID()}`;
  } catch {}
  return `qf-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}
